import React, { useEffect, useState } from "react";
import api, { errMsg } from "@/lib/api";
import { rp, fmtDate, ORDER_STATUS, DISPUTE_STATUS } from "@/lib/format";
import { useApp } from "@/context/AppContext";
import { Layout } from "@/components/Layout";
import { toast } from "sonner";
import { Star, FileText } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";

export default function Orders() {
  const { lang } = useApp();
  const [orders, setOrders] = useState([]);
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const [disputeReason, setDisputeReason] = useState("");

  const load = () => api.get("/orders").then(({ data }) => setOrders(data));
  useEffect(() => { load(); }, []);

  const downloadInvoice = async (o) => {
    const tid = toast.loading("Menyiapkan invoice PDF...");
    try {
      const res = await api.get(`/orders/${o.id}/invoice`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Invoice-${o.order_number || o.id}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.dismiss(tid);
      toast.success("Invoice PDF berhasil diunduh");
    } catch (e) {
      toast.dismiss(tid);
      toast.error(errMsg(e, "Gagal mengunduh invoice"));
    }
  };

  const confirm = async (id) => {
    try { await api.post(`/orders/${id}/confirm-received`); toast.success("Pesanan selesai. Terima kasih!"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const openDispute = async (id) => {
    try { await api.post(`/orders/${id}/dispute`, { reason: disputeReason });
      toast.success("Sengketa diajukan. Tim RELOVA akan menengahi."); setDisputeReason(""); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const review = async (id) => {
    try { await api.post(`/orders/${id}/review`, { rating, comment }); toast.success("Ulasan terkirim"); setComment(""); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-4 sm:px-8 py-8">
        <h1 className="font-serif text-3xl font-semibold text-rl-ink">Pesanan Saya</h1>
        {orders.length === 0 ? <p className="mt-10 text-center text-rl-muted">Belum ada pesanan.</p> : (
          <div className="mt-6 space-y-4">
            {orders.map((o) => {
              const st = ORDER_STATUS[o.status] || ORDER_STATUS.created;
              return (
                <div key={o.id} data-testid={`order-${o.id}`} className="rounded-2xl border border-rl-border bg-white p-5">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div className="text-sm"><span className="font-mono font-semibold text-rl-ink">{o.order_number}</span>
                      <span className="text-rl-muted ml-2">{fmtDate(o.created_at)}</span></div>
                    <div className="flex items-center gap-2">
                      {o.payment_mode === "cod" && <span className="rounded-full px-2 py-0.5 text-[10px] font-bold bg-teal-100 text-teal-800">COD</span>}
                      <span className={`rounded-full px-3 py-0.5 text-xs font-semibold ${st.cls}`}>{st[lang]}</span>
                      {o.dispute_status && o.dispute_status !== "none" && <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${DISPUTE_STATUS[o.dispute_status]?.cls}`}>{DISPUTE_STATUS[o.dispute_status]?.[lang]}</span>}
                    </div>
                  </div>
                  <div className="mt-4 flex gap-4">
                    <div className="w-20 h-20 rounded-lg overflow-hidden bg-rl-bg2 shrink-0">
                      {o.product_snapshot?.photo && <img src={o.product_snapshot.photo} alt="" className="w-full h-full object-cover" />}</div>
                    <div className="flex-1">
                      <div className="font-semibold text-rl-ink">{o.product_snapshot?.title}</div>
                      <div className="text-sm text-rl-muted">Qty: {o.quantity}</div>
                      <div className="font-bold text-rl-ink mt-1">{rp(o.amount)}</div>
                      {o.tracking_number && <div className="text-xs text-rl-secondary mt-1">Resi: {o.courier} · {o.tracking_number}</div>}
                    </div>
                  </div>
                  <div className="mt-4 flex gap-2 flex-wrap items-center">
                    <button
                      data-testid={`invoice-btn-${o.id}`}
                      onClick={() => downloadInvoice(o)}
                      className="inline-flex items-center gap-1.5 rounded-full border border-rl-border hover:border-rl-primary hover:text-rl-primary text-rl-ink text-sm font-semibold px-4 py-2 transition-colors"
                      title="Unduh Invoice PDF"
                    >
                      <FileText className="w-4 h-4 text-rl-primary" /> Invoice PDF
                    </button>
                    {o.status === "shipped" && (
                      <button data-testid={`confirm-received-${o.id}`} onClick={() => confirm(o.id)}
                        className="rounded-full bg-rl-secondary hover:bg-[#2E473D] text-white text-sm font-semibold px-5 py-2 transition-colors">Konfirmasi Diterima</button>
                    )}
                    {o.status === "completed" && (
                      <Dialog>
                        <DialogTrigger asChild>
                          <button data-testid={`review-btn-${o.id}`} className="rounded-full border border-rl-border hover:border-rl-primary text-rl-ink text-sm font-semibold px-5 py-2 transition-colors">Beri Ulasan</button>
                        </DialogTrigger>
                        <DialogContent>
                          <DialogHeader><DialogTitle>Ulas {o.product_snapshot?.title}</DialogTitle></DialogHeader>
                          <div className="flex gap-1">{[1, 2, 3, 4, 5].map((n) => (
                            <button key={n} data-testid={`star-${n}`} onClick={() => setRating(n)}><Star className={`w-7 h-7 ${n <= rating ? "fill-rl-gold text-rl-gold" : "text-rl-border"}`} /></button>))}</div>
                          <textarea data-testid="review-comment" value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Bagikan pengalamanmu..." className="w-full rounded-lg border border-rl-border p-3 text-sm min-h-[90px] outline-none focus:border-rl-primary" />
                          <DialogFooter><button data-testid={`review-submit-${o.id}`} onClick={() => review(o.id)} className="rounded-full bg-rl-primary text-white font-bold px-6 py-2.5">Kirim</button></DialogFooter>
                        </DialogContent>
                      </Dialog>
                    )}
                    {["shipped", "completed"].includes(o.status) && (!o.dispute_status || o.dispute_status === "none") && (
                      <Dialog>
                        <DialogTrigger asChild>
                          <button data-testid={`dispute-btn-${o.id}`} className="rounded-full border border-rose-300 text-rose-600 hover:bg-rose-50 text-sm font-semibold px-5 py-2 transition-colors">Ajukan Sengketa</button>
                        </DialogTrigger>
                        <DialogContent>
                          <DialogHeader><DialogTitle>Ajukan Sengketa — {o.order_number}</DialogTitle></DialogHeader>
                          <p className="text-xs text-rl-muted">Jelaskan masalah (barang tidak sesuai / rusak / tidak diterima). Tim RELOVA akan menengahi dan dana seller ditahan sampai selesai.</p>
                          <textarea data-testid="dispute-reason" value={disputeReason} onChange={(e) => setDisputeReason(e.target.value)} placeholder="Alasan sengketa..." className="w-full rounded-lg border border-rl-border p-3 text-sm min-h-[90px] outline-none focus:border-rl-primary" />
                          <DialogFooter><button data-testid={`dispute-submit-${o.id}`} onClick={() => openDispute(o.id)} disabled={!disputeReason.trim()} className="rounded-full bg-rose-600 text-white font-bold px-6 py-2.5 disabled:opacity-50">Kirim Sengketa</button></DialogFooter>
                        </DialogContent>
                      </Dialog>
                    )}
                  </div>
                  {/* Timeline */}
                  <div className="mt-4 border-t border-rl-border pt-3 space-y-1">
                    {(o.timeline || []).map((t, i) => (
                      <div key={i} className="flex items-center gap-2 text-xs text-rl-muted">
                        <span className="w-1.5 h-1.5 rounded-full bg-rl-primary" />{t.note} · {fmtDate(t.at)}</div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Layout>
  );
}
